'use client';

import * as DropdownMenu from '@radix-ui/react-dropdown-menu';

import { ChoiceChip } from '@eliadmin/core/src/components/dlz/ui/chip';
import { Icon } from '@eliadmin/core/src/components/dlz/ui/icon';
import { REVIEW_COPY } from '@eliadmin/core/src/components/dlz/product-detail/reviews/review-copy';
import {
  STARS,
  type ReviewFilter,
  type ReviewSort,
  type ReviewStats,
} from '@eliadmin/core/src/components/dlz/product-detail/reviews/reviews';

const SORTS: ReviewSort[] = ['newest', 'best', 'worst'];

type ReviewToolbarProps = {
  stats: ReviewStats;
  filter: ReviewFilter;
  onFilter: (filter: ReviewFilter) => void;
  sort: ReviewSort;
  onSort: (sort: ReviewSort) => void;
};

/** Figma „Toolbar“ (60137:8974): filter chips (all, per star, with photo) and the sort dropdown. */
export function ReviewToolbar({ stats, filter, onFilter, sort, onSort }: ReviewToolbarProps) {
  const chips: { value: ReviewFilter; label: string; count: number }[] = [
    { value: 'all', label: REVIEW_COPY.tab.all, count: stats.count },
    ...STARS.map(star => ({
      value: star,
      label: `${star} ★`,
      count: stats.distribution[star],
    })),
    ...(stats.withPhoto
      ? [{ value: 'photo' as const, label: REVIEW_COPY.tab.withPhoto, count: stats.withPhoto }]
      : []),
  ];

  return (
    <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between lg:gap-4">
      <div
        role="group"
        aria-label={REVIEW_COPY.tab.filters}
        // The vertical padding keeps the focus ring of a chip inside the scroll container; on desktop the chips wrap instead of scrolling.
        className="hide-scroll-bar -mx-4 -my-1 flex min-w-0 gap-2 overflow-x-auto px-4 py-1 md:-mx-1 md:px-1 lg:flex-wrap"
      >
        {chips.map(chip => (
          <ChoiceChip
            key={chip.value}
            selected={filter === chip.value}
            disabled={chip.count === 0}
            onSelect={() => onFilter(chip.value)}
            className="h-10 gap-1.5 whitespace-nowrap md:h-[34px]"
          >
            {chip.label} ({chip.count})
          </ChoiceChip>
        ))}
      </div>

      <DropdownMenu.Root>
        <DropdownMenu.Trigger className="dlz-body-s -m-1 flex shrink-0 items-center gap-1.5 self-start rounded-lg p-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0F1012] lg:self-auto">
          <span className="text-[#6B7079]">{REVIEW_COPY.tab.sortBy}</span>
          <span className="font-semibold text-[#0F1012]">{REVIEW_COPY.sort[sort]}</span>
          <Icon name="caret-down" size={16} className="text-[#0F1012]" />
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            align="end"
            sideOffset={8}
            className="z-50 min-w-[180px] rounded-[18px] border border-[#E2E4E7] bg-white p-2 shadow-dropdown data-[state=open]:animate-in data-[state=open]:fade-in-0 motion-reduce:animate-none"
          >
            <DropdownMenu.RadioGroup
              value={sort}
              onValueChange={value => onSort(value as ReviewSort)}
            >
              {SORTS.map(option => (
                <DropdownMenu.RadioItem
                  key={option}
                  value={option}
                  className="dlz-body-s flex h-10 cursor-pointer items-center justify-between gap-3 rounded-xl px-3 text-[#0F1012] outline-none data-[highlighted]:bg-[#F5F6F7] data-[state=checked]:font-semibold"
                >
                  {REVIEW_COPY.sort[option]}
                  <DropdownMenu.ItemIndicator>
                    <Icon name="check" size={16} />
                  </DropdownMenu.ItemIndicator>
                </DropdownMenu.RadioItem>
              ))}
            </DropdownMenu.RadioGroup>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </div>
  );
}
